<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { inject, ref, onMounted, computed } from "vue"
import { useI18n } from "vue-i18n"
import { useFetchWrapper, useResponsive } from "@core/composables"
import { useAuthStore } from "@core/stores/auth.store"
import type { Services } from "@services"
import type { CompanyUser } from "@core/services/user.service"
import type { ColumnConfig } from "@types"
import { useAccessBuildingByUserDialog } from "@modules/espace-entreprise/composables/useAccessBuildingByUserDialog"
import { useModalCompanyAddMember } from "@modules/espace-entreprise/composables/useModalCompanyAddMember"
import { useCompanyMemberActions } from "@modules/espace-entreprise/composables/useCompanyMemberActions"
import { capitalize } from "@core/helpers"

// ================================
// TYPES & INTERFACES
// ================================

type FilterOption = {
    label: string
    value: "all" | "administrateur" | "employe" | "employe_read_only"
}

type Props = {
    /** Afficher le bouton "Ajouter un membre" et permettre d'ouvrir la modal */
    canAddMember?: boolean
    /** Permettre de modifier le rôle d'un membre (sélecteur de rôle) */
    canChangeRole?: boolean
    /** Permettre de supprimer un membre (bouton supprimer) */
    canDeleteUser?: boolean
    /** Permettre de gérer les accès aux bâtiments (bouton "Gérer les accès") */
    canShareBuilding?: boolean
}

// ================================
// PROPS & EMITS
// ================================

const props = withDefaults(defineProps<Props>(), {
    canAddMember: true,
    canChangeRole: true,
    canDeleteUser: true,
    canShareBuilding: true,
})

// ================================
// SERVICES & STORES
// ================================

/**
 * Injection des services
 */
const { $getCompanyUsers } = inject<Services>("services")!

/**
 * Store d'authentification pour vérifier les permissions
 */
const authStore = useAuthStore()

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Membres",
            members_load_error:
                "Nous rencontrons un problème pour afficher vos membres pour le moment. Veuillez réessayer plus tard.",
            add_user: "Ajouter un membre",
            placeholder: "Rechercher un nom, une adresse mail...",
            empty: "Aucun membre trouvé",
            no_member: (ctx: { search?: string }) =>
                ctx.search
                    ? `Aucun membre trouvé pour "${ctx.search}"`
                    : "Aucun membre trouvé",
            total: (ctx: { nb?: string }) =>
                `${ctx.nb ?? 0} membre${Number(ctx.nb) > 1 ? "s" : ""}`,
            admin: (ctx: { nb?: string }) =>
                `${ctx.nb ?? 0} administrateur${Number(ctx.nb) > 1 ? "s" : ""}`,
            employe: (ctx: { nb?: string }) =>
                `${ctx.nb ?? 0} employé${Number(ctx.nb) > 1 ? "s" : ""}`,
            employe_read_only: (ctx: { nb?: string }) =>
                `${ctx.nb ?? 0} utilisateur${Number(ctx.nb) > 1 ? "s" : ""}`,
            filter: {
                all: "Tous",
                administrateur: "Administrateurs",
                employe: "Employés",
                employe_read_only: "Utilisateurs",
            },
            column: {
                name: "Nom",
                contact: "Contact",
                role: "Rôle",
                actions: "Actions",
            },
            list_item: {
                delete: "Supprimer le compte",
                access: "Gérer les accès",
                warning_role:
                    "Vous ne pouvez pas modifier le rôle d'une personne qui est également invitée dans votre espace.",
            },
            role_info_link: "Comprendre les rôles",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const { isMobile } = useResponsive()

// ================================
// STATE
// ================================

/**
 * Liste des membres de l'entreprise
 */
const members = ref<CompanyUser[]>([])

/**
 * Liste des clients de l'entreprise
 */
const clients = ref<CompanyUser[]>([])

/**
 * Composable : dialog d'accès aux bâtiments par utilisateur
 */
const {
    userToShareBuilding,
    dialogVisible: dialogBuildingAccessVisible,
    buildings,
    isFetchingBuildings,
    openDialog: onAccessBuilding,
    onUpdateBuildingAccess,
} = useAccessBuildingByUserDialog()

/**
 * Composable : modal d'ajout de membre
 */
const { visible: modalAddMemberVisible, openModal: openModalAddMember } =
    useModalCompanyAddMember()

/**
 * Composable : changement de rôle et suppression d'un membre
 */
const { componentId, onChangeRole, onDeleteUser } =
    useCompanyMemberActions(members)

/**
 * Valeur du filtre par rôle (all / administrateur / employe / employe_read_only)
 */
const filterValue = ref<
    "all" | "administrateur" | "employe" | "employe_read_only"
>("all")

/**
 * Valeur de la recherche globale (filtre côté client)
 */
const filters = ref({
    global: { value: null as string | null },
})

/**
 * Visibilité de la dialog d'explication des rôles
 */
const roleInfoDialogVisible = ref(false)

// ================================
// FETCH WRAPPERS
// ================================

/**
 * Chargement des membres et clients
 */
const {
    fetch: fetchMembers,
    isFetching,
    hasError,
} = useFetchWrapper(async () => {
    if (!$getCompanyUsers) {
        throw new Error("Service $getCompanyUsers non disponible")
    }

    // Récupère les membres (tous les types sauf client)
    const membersList = await $getCompanyUsers([
        "employe",
        "administrateur",
        "employe_read_only",
        "owner",
    ])

    // Garantit que membersList est un tableau
    const safeMembersList = Array.isArray(membersList) ? membersList : []

    // Trie les membres par rôle : owner → administrateur → employe → employe_read_only
    const roleOrder = [
        "owner",
        "administrateur",
        "employe",
        "employe_read_only",
    ]
    members.value = [...safeMembersList].sort((a, b) => {
        return roleOrder.indexOf(a.type) - roleOrder.indexOf(b.type)
    })

    // Récupère les invités
    const clientsList = await $getCompanyUsers(["client"])
    clients.value = Array.isArray(clientsList) ? clientsList : []
})

// ================================
// COMPUTED
// ================================

/**
 * Indique si le bouton d'ajout de membre doit être affiché (prop fournie par le parent)
 */
const getShouldShowAddMemberButton = computed(() => props.canAddMember)

/**
 * Liste des membres normalisée (garantit un tableau)
 */
const getNormalizedMembers = computed(() => {
    if (!members.value || !Array.isArray(members.value)) {
        return []
    }
    return members.value
})

/**
 * Nombre total de membres
 */
const getMembersCount = computed(() => getNormalizedMembers.value?.length ?? 0)

/**
 * Nombre d'administrateurs (owner + administrateur)
 */
const getNbAdmin = computed(
    () =>
        getNormalizedMembers.value.filter((item) =>
            ["owner", "administrateur"].includes(item.type)
        ).length
)

/**
 * Nombre d'employés
 */
const getNbEmploye = computed(
    () =>
        getNormalizedMembers.value.filter((item) => item.type === "employe")
            .length
)

/**
 * Nombre d'utilisateurs en lecture seule
 */
const getNbEmployeReadOnly = computed(
    () =>
        getNormalizedMembers.value.filter(
            (item) => item.type === "employe_read_only"
        ).length
)

/**
 * Options de filtre par rôle (avec effectif entre parenthèses)
 */
const getFilterOptions = computed<FilterOption[]>(() => [
    {
        label: `${t("filter.all")} (${getMembersCount.value})`,
        value: "all",
    },
    {
        label: `${t("filter.administrateur")} (${getNbAdmin.value})`,
        value: "administrateur",
    },
    {
        label: `${t("filter.employe")} (${getNbEmploye.value})`,
        value: "employe",
    },
    {
        label: `${t("filter.employe_read_only")} (${getNbEmployeReadOnly.value})`,
        value: "employe_read_only",
    },
])

/**
 * Liste des membres filtrés (par rôle et recherche)
 */
const getMembersFiltered = computed(() => {
    let filtered = [...getNormalizedMembers.value]
    if (filterValue.value !== "all") {
        if (filterValue.value === "administrateur") {
            filtered = filtered.filter((item) =>
                ["owner", "administrateur"].includes(item.type)
            )
        } else {
            filtered = filtered.filter(
                (item) => item.type === filterValue.value
            )
        }
    }
    const searchValue = filters.value.global.value
    if (searchValue && typeof searchValue === "string") {
        const searchLower = searchValue.toLowerCase().trim()
        if (searchLower) {
            filtered = filtered.filter((item) => {
                const firstname = String(item.firstname || "").toLowerCase()
                const lastname = String(item.lastname || "").toLowerCase()
                const username = String(item.username || "").toLowerCase()
                return (
                    firstname.startsWith(searchLower) ||
                    lastname.startsWith(searchLower) ||
                    username.startsWith(searchLower)
                )
            })
        }
    }
    return filtered
})

/**
 * Message affiché lorsque la liste est vide
 */
const getEmptyMessage = computed(() => {
    const searchValue = filters.value.global.value
    if (searchValue) {
        return t("no_member", { search: searchValue })
    }
    return t("empty")
})

/**
 * Configuration des colonnes pour BaseTable
 */
const getMemberColumns = computed<ColumnConfig[]>(() => {
    const columns: ColumnConfig[] = [
        {
            field: "name",
            header: t("column.name"),
            sortable: false,
            style: { width: "16rem" },
        },
        {
            field: "contact",
            header: t("column.contact"),
            sortable: false,
            style: { width: "18rem" },
        },
        {
            field: "role",
            header: t("column.role"),
            sortable: false,
        },
    ]
    if (props.canDeleteUser || props.canShareBuilding) {
        columns.push({
            field: "actions",
            header: t("column.actions"),
            sortable: false,
        })
    }
    return columns
})
// ================================
// HELPERS (liste membres)
// ================================

/**
 * Vérifie si le changement de rôle est désactivé pour un membre (également invité)
 */
function getDisabledRole(member: CompanyUser): boolean {
    return !!clients.value.find((client) => client._id === member._id)
}

/**
 * Retourne les initiales du membre pour l'avatar
 */
function getMemberInitials(member: CompanyUser): string {
    const firstname = member?.firstname || ""
    const lastname = member?.lastname || ""
    return `${firstname.charAt(0)}${lastname.charAt(0)}`.toUpperCase()
}

/**
 * Retourne le nom complet du membre
 */
function getMemberFullName(member: CompanyUser): string {
    const firstname = capitalize(member?.firstname || "")
    const lastname = capitalize(member?.lastname || "")
    return `${firstname} ${lastname}`.trim()
}

/**
 * Options de rôle disponibles (sans owner)
 */
const roleOptions = [
    {
        label: "Admin",
        value: "administrateur",
        description: "Accès à toutes les informations de l'entreprise",
    },
    {
        label: "Membre",
        value: "employe",
        description:
            "Ne peut ni créer de dossier ni gérer les partages des bâtiments",
    },
    {
        label: "Utilisateur",
        value: "employe_read_only",
        description: "Lecture seule",
    },
]

/**
 * Liste complète des rôles avec labels (pour affichage)
 */
const allRoles = [
    { label: "Super Admin", value: "owner" },
    { label: "Admin", value: "administrateur" },
    { label: "Membre", value: "employe" },
    { label: "Utilisateur", value: "employe_read_only" },
]

/**
 * Retourne le label d'un rôle
 */
function getRoleLabel(role: CompanyUser["type"]): string {
    return allRoles.find((r) => r.value === role)?.label || role
}

// ================================
// INTERACTIONS
// ================================

/**
 * Gère l'ajout d'un membre (recharge la liste)
 */
function onMemberAdded() {
    fetchMembers()
}

// ================================
// LIFECYCLE
// ================================

/**
 * Charge les membres et les bâtiments au montage
 */
onMounted(() => {
    fetchMembers()
})
</script>

<template>
    <BaseTitledCard class="company-members">
        <template #header>
            <div
                class="flex justify-between items-center w-full flex-wrap gap-2"
            >
                <div class="flex items-center gap-3 min-w-0">
                    <span>{{ t("title") }}</span>
                    <BaseButton
                        :label="t('role_info_link')"
                        variant="link"
                        severity="secondary"
                        size="small"
                        class="p-0 text-sm font-normal"
                        @click="roleInfoDialogVisible = true"
                    />
                </div>
                <BaseButton
                    v-if="getShouldShowAddMemberButton"
                    :label="t('add_user')"
                    icon="pi pi-plus"
                    severity="primary"
                    size="small"
                    @click="openModalAddMember()"
                />
            </div>
        </template>
        <!-- ================================ -->
        <!-- CONTENT -->
        <!-- ================================ -->
        <template #content>
            <div class="flex flex-col gap-4">
                <!-- Message d'erreur -->
                <BaseMessage v-if="hasError" severity="error">
                    {{ t("members_load_error") }}
                </BaseMessage>
                <!-- Skeleton pendant le chargement -->
                <BaseSkeleton
                    v-else-if="isFetching"
                    width="100%"
                    height="30vh"
                    class="mx-auto"
                />
                <!-- Liste des membres -->
                <div v-else class="company-members-list flex flex-col gap-4">
                    <div
                        class="flex flex-wrap items-center justify-between gap-4 mb-4"
                    >
                        <BaseInputWrapper
                            :label="t('placeholder')"
                            input-id="input-search-members"
                            class="md:max-w-[37.5rem] w-full min-w-[18rem]"
                        >
                            <BaseInputText
                                v-model="filters.global.value"
                                input-id="input-search-members"
                            />
                        </BaseInputWrapper>
                        <BaseSelect
                            v-if="isMobile"
                            v-model="filterValue"
                            :options="getFilterOptions"
                            option-label="label"
                            option-value="value"
                            class="w-full"
                        />
                        <BaseSelectButton
                            v-else
                            v-model="filterValue"
                            :options="getFilterOptions"
                            option-label="label"
                            option-value="value"
                        />
                    </div>
                    <BaseTable
                        data-key="_id"
                        :columns="getMemberColumns"
                        :value="getMembersFiltered"
                        paginator
                        :rows="10"
                        :rows-per-page-options="[10, 25, 50]"
                        :empty-message="getEmptyMessage"
                        size="small"
                    >
                        <template #cell_name="{ data }">
                            <div class="flex items-center gap-4 min-w-0">
                                <figure
                                    v-if="!data?.img"
                                    class="flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-medium text-sm uppercase w-8 h-8"
                                >
                                    {{ getMemberInitials(data) }}
                                </figure>
                                <img
                                    v-else
                                    :src="data.img"
                                    :alt="getMemberFullName(data)"
                                    class="flex-shrink-0 rounded-full object-cover w-8 h-8"
                                />
                                <p
                                    class="capitalize min-w-0 truncate"
                                    :title="getMemberFullName(data)"
                                >
                                    {{ getMemberFullName(data) }}
                                    <span
                                        v-if="data._id === authStore.userId"
                                        class="text-gray-500"
                                    >
                                        (vous)
                                    </span>
                                </p>
                            </div>
                        </template>
                        <template #cell_contact="{ data }">
                            <p
                                class="lowercase truncate"
                                :title="data?.username"
                            >
                                {{ data?.username }}
                            </p>
                        </template>
                        <template #cell_role="{ data }">
                            <div class="flex items-center gap-2 min-w-0">
                                <BaseSelect
                                    v-if="
                                        authStore.userId !== data._id &&
                                        props.canChangeRole &&
                                        data.type !== 'owner' &&
                                        !getDisabledRole(data)
                                    "
                                    :model-value="data.type"
                                    :options="roleOptions"
                                    option-label="label"
                                    option-value="value"
                                    placeholder="Changer"
                                    class="min-w-[9rem]"
                                    @update:model-value="
                                        onChangeRole({
                                            userId: data._id,
                                            role: $event,
                                        })
                                    "
                                >
                                    <template #value="selectProps">
                                        <span v-if="selectProps.value">
                                            {{
                                                roleOptions.find(
                                                    (opt) =>
                                                        opt.value ===
                                                        selectProps.value
                                                )?.label
                                            }}
                                        </span>
                                        <span v-else class="text-gray-400">
                                            Changer
                                        </span>
                                    </template>
                                    <template #option="selectProps">
                                        <div class="flex flex-col py-2">
                                            <span class="font-medium">
                                                {{ selectProps.option.label }}
                                            </span>
                                            <span
                                                v-if="
                                                    selectProps.option
                                                        .description
                                                "
                                                class="text-xs text-gray-500 mt-1"
                                            >
                                                {{
                                                    selectProps.option
                                                        .description
                                                }}
                                            </span>
                                        </div>
                                    </template>
                                </BaseSelect>
                                <p
                                    v-else
                                    class="min-w-0 truncate"
                                    :title="getRoleLabel(data.type)"
                                    v-tooltip.top="
                                        getDisabledRole(data)
                                            ? t('list_item.warning_role')
                                            : null
                                    "
                                >
                                    {{ getRoleLabel(data.type) }}
                                </p>
                            </div>
                        </template>
                        <template #cell_actions="{ data }">
                            <div
                                v-if="
                                    props.canDeleteUser ||
                                    props.canShareBuilding
                                "
                                class="flex items-center gap-2"
                            >
                                <BaseButton
                                    v-if="
                                        props.canDeleteUser &&
                                        data._id !== authStore.userId &&
                                        (data.type !== 'owner' ||
                                            (data.type === 'owner' &&
                                                authStore.role === 'owner'))
                                    "
                                    :id="`company-members-list-item-${data._id}-delete`"
                                    icon="pi pi-trash"
                                    severity="secondary"
                                    text
                                    rounded
                                    size="small"
                                    v-tooltip.top="t('list_item.delete')"
                                    @click="onDeleteUser(data)"
                                />
                                <BaseButton
                                    v-if="
                                        props.canShareBuilding &&
                                        data.type !== 'administrateur' &&
                                        data.type !== 'owner'
                                    "
                                    :id="`company-members-list-item-${data._id}-access`"
                                    icon="pi pi-unlock"
                                    severity="secondary"
                                    text
                                    rounded
                                    size="small"
                                    v-tooltip.top="t('list_item.access')"
                                    @click="onAccessBuilding(data)"
                                />
                            </div>
                        </template>
                        <template #empty>
                            <div class="p-8 text-center text-gray-600">
                                {{ getEmptyMessage }}
                            </div>
                        </template>
                    </BaseTable>
                </div>
            </div>
            <!-- Dialog d'accès aux bâtiments -->
            <AccessBuildingByUserDialog
                v-if="userToShareBuilding"
                v-model:visible="dialogBuildingAccessVisible"
                :user-id="userToShareBuilding._id"
                :firstname="userToShareBuilding.firstname"
                :lastname="userToShareBuilding.lastname"
                :username="userToShareBuilding.username"
                :avatar="userToShareBuilding.img"
                :loading="isFetchingBuildings"
                :buildings="buildings"
                :admin="userToShareBuilding.type === 'administrateur'"
                @update:access="onUpdateBuildingAccess"
            />
            <!-- BaseConfirmDialog pour les confirmations -->
            <BaseConfirmDialog :group="componentId" />
            <!-- Modal d'ajout de membre -->
            <CompanyAddMemberDialog
                v-if="getShouldShowAddMemberButton"
                v-model:visible="modalAddMemberVisible"
                :members="members"
                :clients="clients"
                @member:added="onMemberAdded"
            />
            <!-- Dialog d'explication des rôles -->
            <CompanyRoleInformationDialog
                v-model:visible="roleInfoDialogVisible"
            />
        </template>
    </BaseTitledCard>
</template>
